<script lang="ts" setup>
import type { FieldFilter, FilterRelationalQuantifier, PrimitiveFieldPath } from '#layers/data-model/public';
import type { SegmentedFieldFilter } from './utils/filter-path';
import type {
	FilterSchemaCollection,
	FilterSchemaPrimitiveField,
} from './utils/filter-schema';
import type { UIFilterOperator } from './utils/filter-ui-operator';
import { createReusableTemplate, until } from '@vueuse/core';
import { useEmitAsProps } from 'reka-ui';
import { computed, nextTick, onBeforeUnmount, ref, useTemplateRef, watch } from 'vue';
import { delay } from '#layers/base/public';
import { FilterOperator, getFieldFilterOperatorValue, useFieldTypeTemplate } from '#layers/data-model/public';
import { getNarrowedOperatorsForField, resolveDefaultOperator, useFilterOperator } from '#layers/filters/composables/use-filter-operator';
import { buildSegmentsFromFieldIds, isPrimitiveSegment, isRelationalSegment, parseSegmentedFilter, serializeSegmentedFilter } from './utils/filter-path';
import { injectFilterSchemaContext } from './utils/filter-schema-context';
import { modelToUi, UI_NOT_NULL, uiToModel } from './utils/filter-ui-operator';
import { useFilterConfigResolver } from './utils/use-filter-config-resolver';

const props = withDefaults(defineProps<{
	collection: FilterSchemaCollection;
	includeRelationFields?: boolean;
	hideGroupActions?: boolean;
	disabled?: boolean;
	readonly?: boolean;
	autoAdvance?: boolean;
}>(), {
	includeRelationFields: true,
});

const emits = defineEmits<{
	remove: [];
	duplicate: [];
	turnIntoGroup: [];
	autoAdvanceDone: [];
}>();

const filter = defineModel<FieldFilter>({ required: true });
const { operator, getOperatorLabel } = useFilterOperator(filter);
const schema = injectFilterSchemaContext();
const { findPrimitiveFieldById } = schema;
const actionEmits = useEmitAsProps(emits);

const internalFilter = computed({
	get(): SegmentedFieldFilter | null {
		try {
			return parseSegmentedFilter(filter.value, props.collection, schema);
		}
		catch {
			return null;
		}
	},
	set(newFilter: SegmentedFieldFilter | null) {
		if (!newFilter) return;
		filter.value = serializeSegmentedFilter(newFilter, schema);
	},
});

const segments = computed(() => internalFilter.value?.segments ?? []);

function onFieldPathSelect(index: number, fieldPath: PrimitiveFieldPath) {
	if (!internalFilter.value) return;

	const keptSegments = internalFilter.value.segments.slice(0, index);
	const lastKeptSegment = keptSegments.at(-1);
	const startCollection = lastKeptSegment && isRelationalSegment(lastKeptSegment)
		? schema.findOppositeCollectionByRelationFieldId(lastKeptSegment.fieldId) ?? props.collection
		: props.collection;
	const newSegments = buildSegmentsFromFieldIds(fieldPath, startCollection, schema);
	const lastSegment = newSegments.at(-1);

	if (newSegments.length > 0) {
		const seedOperator = lastSegment && isPrimitiveSegment(lastSegment) ? getDefaultOperatorOption(findPrimitiveFieldById(lastSegment.fieldId)) : undefined;
		const seed = seedOperator ? uiToModel(seedOperator, { operator: undefined, value: undefined }) : { operator: undefined, value: undefined };

		internalFilter.value = {
			segments: [...keptSegments, ...newSegments],
			operator: seed.operator,
			value: seed.value,
		};
	}
}

function onUpdateQuantifier(index: number, quantifier: FilterRelationalQuantifier) {
	if (!internalFilter.value) return;

	const newSegments = [...internalFilter.value.segments];
	const segment = newSegments[index];

	if (segment?.type === 'to-many') {
		newSegments[index] = { ...segment, quantifier };
		internalFilter.value = { ...internalFilter.value, segments: newSegments };
	}
}

const primitiveField = computed(() => {
	const finalSegment = segments.value.at(-1);

	if (finalSegment && isPrimitiveSegment(finalSegment)) {
		return findPrimitiveFieldById(finalSegment.fieldId);
	}

	return null;
});

const { resolveFilterConfig } = useFilterConfigResolver();
const { typeOptions } = useFieldTypeTemplate(computed(() => primitiveField.value ?? null));

const filterConfig = computed(() => resolveFilterConfig(primitiveField.value ?? undefined));

const operatorOpen = ref(false);
const inputGroupRef = useTemplateRef<{ focusFirstInput: () => void }>('inputGroupRef');
const pendingInputFocus = ref(false);
const hasCustomInputGroup = computed(() => Boolean(filterConfig.value?.inputGroup));
let advanceToken = 0;

defineExpose({ operatorOpen });

interface AdvanceStep {
	isApplicable: boolean;
	activate: () => void;
	waitUntilPast: () => Promise<void>;
}

const advanceSteps = computed<AdvanceStep[]>(() => [
	{
		isApplicable: !hasCustomInputGroup.value,
		activate: () => {
			operatorOpen.value = true;
		},
		waitUntilPast: async () => {
			await until(operatorOpen).toBe(false);
		},
	},
	{
		isApplicable: true,
		activate: () => {
			focusFirstInput();
		},
		waitUntilPast: async () => {},
	},
]);

function focusFirstInput() {
	if (!inputGroupRef.value) {
		pendingInputFocus.value = true;
		return;
	}

	pendingInputFocus.value = false;
	inputGroupRef.value.focusFirstInput();
}

watch(inputGroupRef, (inputGroup) => {
	if (!inputGroup || !pendingInputFocus.value) return;

	focusFirstInput();
});

watch(() => props.autoAdvance, (active) => {
	if (!active) return;
	const token = ++advanceToken;
	void runAdvance(token);
}, { immediate: true });

async function runAdvance(token: number) {
	for (let i = 0; i < advanceSteps.value.length; i++) {
		const step = () => advanceSteps.value[i]!;
		if (!step().isApplicable) continue;
		// Custom groups skip the operator step, so their focus target only exists after the initial render.
		if (i > 0) await nextTick();
		step().activate();
		await step().waitUntilPast();
		if (token !== advanceToken) return;
		// reka-ui restores focus in setTimeout(0); beat it
		await delay(0);
		if (token !== advanceToken) return;
	}

	if (token === advanceToken) {
		emits('autoAdvanceDone');
	}
}

onBeforeUnmount(() => {
	advanceToken++;
});

const isInvalidFilter = computed(() => {
	if (internalFilter.value === null) {
		return true;
	}

	const finalSegment = segments.value.at(-1);

	if (!finalSegment) {
		return true;
	}

	if (finalSegment && isPrimitiveSegment(finalSegment) && primitiveField.value == null) {
		return true;
	}

	return false;
});

const operatorOptions = computed<{ label: string; value: UIFilterOperator }[]>(() => getOperatorOptions(primitiveField.value ?? undefined));

const writableOperator = computed<UIFilterOperator | undefined>({
	get: () => modelToUi(operator.value, getFieldFilterOperatorValue(filter.value)),
	set(op) {
		if (op === undefined || !internalFilter.value) return;

		const next = uiToModel(op, { operator: writableOperator.value, value: internalFilter.value.value });
		internalFilter.value = { ...internalFilter.value, ...next };
	},
});

function resolveFieldOperators(field?: FilterSchemaPrimitiveField) {
	const config = resolveFilterConfig(field);
	const operators = getNarrowedOperatorsForField(field, config?.supportedOperators);

	return { config, operators };
}

/**
 * Operator a newly added filter seeds with: the field-type's explicit `defaultOperator` when set
 * (decoupled from dropdown order), else the first option. Falls back to the first option value for
 * nullable-only fields whose narrowed operator list is empty (only `_null` / `isn't null` remain).
 */
function getDefaultOperatorOption(field?: FilterSchemaPrimitiveField): UIFilterOperator | undefined {
	const { config, operators } = resolveFieldOperators(field);

	if (operators.length === 0) return getOperatorOptions(field)?.[0]?.value;

	return resolveDefaultOperator(operators, config?.defaultOperator);
}

function getOperatorOptions(field?: FilterSchemaPrimitiveField) {
	const { config, operators } = resolveFieldOperators(field);
	const operatorLabels = config?.operatorLabels;

	return [
		...operators.map((op) => ({
			label: getOperatorLabel(op, operatorLabels),
			value: op,
		})),
		// "is null" / "isn't null" for nullable fields — both map to the `_null` operator
		// at the model layer (true / false respectively), see filter-ui-operator.ts.
		...(field?.isNullable
			? [
					{
						label: getOperatorLabel(FilterOperator.NULL, operatorLabels),
						value: FilterOperator.NULL,
					},
					{
						label: getOperatorLabel(UI_NOT_NULL, operatorLabels),
						value: UI_NOT_NULL,
					},
				] as const
			: []
		),
	];
}

const [DefineFilterError, ReuseFilterError] = createReusableTemplate();

/**
 * A caught render error must still leave a row behind.
 *
 * The boundary swallows the throw and renders the `#error` slot in place of the condition, so a
 * slot that states nothing turns a broken row into an absent one — on a permissions editor that
 * reads as a rule that isn't there. The filter value itself is untouched and still saves, so the
 * row states that it cannot be shown and offers the one edit that always applies: removing it.
 */
function onRenderError(error: unknown) {
	console.error('[FilterSimple] Failed to render a filter condition', error);
}
</script>

<template>
	<DefineFilterError>
		<FilterHandleGroup class="border-error">
			<div class="flex items-center gap-1.5 px-2 text-error">
				<DIcon name="lucide:circle-alert" class="size-4" />
				<span class="text-xs">{{ $t('filters.invalid_filter') }}</span>
			</div>
			<FilterRemove v-bind="actionEmits" :disabled />
		</FilterHandleGroup>
	</DefineFilterError>

	<NuxtErrorBoundary @error="onRenderError">
		<template #error>
			<ReuseFilterError />
		</template>

		<ReuseFilterError v-if="isInvalidFilter" />

		<FilterHandleGroup v-else>
			<FilterFieldPath
				:segments
				:disabled
				:include-relation-fields
				@update-from-segment="onFieldPathSelect"
				@update-quantifier="onUpdateQuantifier"
			/>

			<DSelectMenu
				v-model="writableOperator"
				v-model:open="operatorOpen"
				:items="operatorOptions"
				value-key="value"
				variant="ghost"
				trailing-icon=""
				class="rounded-none"
				:search-input="operatorOptions.length > 5"
				:ui="{
					base: 'px-2',
					content: 'min-w-fit',
					value: 'text-xs',
				}"
				:disabled
			/>
			<FilterInputGroup
				ref="inputGroupRef"
				v-model="internalFilter"
				:primitive-field="primitiveField!"
				:filter-config
				:type-options
				:disabled
				:readonly
				@remove="emits('remove')"
			/>
			<FilterActions v-if="hideGroupActions && !readonly" v-bind="actionEmits" can-turn-into-group :disabled />
			<FilterRemove v-else-if="!readonly" v-bind="actionEmits" :disabled />
		</FilterHandleGroup>
	</NuxtErrorBoundary>
</template>
